import { History } from 'history';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';

import EnrolmentLayout from '../../../../common/hub/EnrolmentLayout';
import { OptionCard, OptionCardContent } from '../../../../components/EnrolmentManagement/OptionCard';
import { IntentIntro } from '../../../../components/IntentIntro';
import { Loader } from '../../../../components/Loader';
import { useEnrolmentDetailQuery } from '../../../../hooks/queries/useEnrolmentQuery';
import { useGetSessionPolicy } from '../../../../hooks/queries/useSessionQuery';
import { getCancelEnrolmentBasePath } from '../../../../selectors/enrolmentSelector';
import { getAccountDetails } from '../../../../selectors/hubSelector';
import { getSchoolCurrentHoliday } from '../../../../selectors/schoolTerms/schoolTermsSelectors';
import { ELEMENT_TYPES } from '../../../../types/hubEnums';
import { AccountDetails, SessionPolicy } from '../../../../types/hubInterfaces';
import { InitialState, SchoolHolidayTerm } from '../../../../types/hubTypes';
import { isEnrolmentPausedCheck, isGroupSessionCheck } from '../../../../util/helpers';
import {
  PATH_HUB_ADD_ONE_OFF_SESSION,
  PATH_HUB_CR_PAUSE_SESSIONS,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP,
  PATH_HUB_HOME,
  PATH_HUB_SELECT_SESSION,
} from '../../../../util/pagePath';
import { PostHogEvent } from '../../../../util/posthog';
import { usePageHeader } from '../../../../v2/app-shell/usePageHeader';
import useCanPauseEnrolment from '../../../../v2/features/pause-enrolment/hooks/useCanPauseEnrolment';

type Option = {
  label: string;
  linkTo?: string;
  onClickHandler?: () => void;
  type: ELEMENT_TYPES;
  posthogEvent?: PostHogEvent;
};

const generateOptions = ({
  enrolmentId,
  tutorName,
  enrolmentType,
  enrolmentStatus,
  history,
  cancelEnrolmentBasePath,
  enrolmentCanBePaused,
}: {
  enrolmentId: string;
  tutorName: string;
  enrolmentType: string;
  enrolmentStatus: string;
  history: History;
  cancelEnrolmentBasePath: string;
  enrolmentPolicy: SessionPolicy;
  schoolCurrentHoliday: SchoolHolidayTerm;
  enrolmentCanBePaused: boolean;
}): Option[] => {
  const isGroup = isGroupSessionCheck(enrolmentType);
  const isPaused = isEnrolmentPausedCheck({ enrolmentStatus });
  const changeSchedulePath = isGroup ? PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP : PATH_HUB_ENROLMENT_CHANGE_SCHEDULE;

  let options: Option[] = [
    {
      label: 'Reschedule a session',
      onClickHandler: () => {
        history.push(generatePath(PATH_HUB_SELECT_SESSION, { enrolmentId }), { action: 'reschedule' });
      },
      type: ELEMENT_TYPES.BUTTON,
      posthogEvent: 'click_session-times_reschedule-session',
    },
    {
      label: 'Cancel a session',
      onClickHandler: () => {
        history.push(generatePath(PATH_HUB_SELECT_SESSION, { enrolmentId }), { action: 'cancel' });
      },
      type: ELEMENT_TYPES.BUTTON,
      posthogEvent: 'click_session-times_cancel-session',
    },
    {
      label: `Add a session with ${tutorName}`,
      linkTo: generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, { enrolmentId }),
      type: ELEMENT_TYPES.LINK,
      posthogEvent: 'click_session-times_add-one-off-session',
    },
    {
      label: 'Manage ongoing session times and frequency',
      linkTo: `${changeSchedulePath}?token=${enrolmentId}`,
      type: ELEMENT_TYPES.LINK,
      posthogEvent: 'click_session-times_change-schedule',
    },
    {
      label: 'Pause enrolment',
      linkTo: `${PATH_HUB_CR_PAUSE_SESSIONS}?token=${enrolmentId}`,
      type: ELEMENT_TYPES.LINK,
      posthogEvent: 'click_session-times_pause-enrolment',
    },
    {
      label: 'Cancel enrolment',
      linkTo: `${cancelEnrolmentBasePath}?view=form&token=${enrolmentId}`,
      type: ELEMENT_TYPES.LINK,
      posthogEvent: 'click_session-times_cancel-enrolment',
    },
  ];
  // Add a one-off session is only available for private active enrolments
  if (isGroup || isPaused) {
    options = options.filter((op) => !op.label.includes('Add a session'));
  }

  if (!enrolmentCanBePaused) {
    options = options.filter((op) => !op.label.includes('Pause enrolment'));
  }

  return options;
};

export const ManageServiceSessionTimes = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const {
    data: enrolmentDetail,
    isLoading: isEnrolmentDetailsLoading,
    error,
  } = useEnrolmentDetailQuery({ enrolmentId });
  const history = useHistory();
  const { setHeader } = usePageHeader();

  if (!enrolmentId) {
    history.push(PATH_HUB_HOME);
  }

  useEffect(() => {
    setHeader({
      label: 'Session times and schedules',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const {
    studentName,
    customerNumber: studentCustomerNumber,
    enrolmentDiscipline,
    enrolmentType,
    enrolmentStatus,
    tutorName,
    enrolmentSfid,
  } = enrolmentDetail ?? {};

  const { customerNumber }: AccountDetails = useSelector(getAccountDetails);
  const possessiveNoun = customerNumber === studentCustomerNumber ? 'your' : `${studentName}'s`;
  const cancelEnrolmentBasePath = useSelector(getCancelEnrolmentBasePath);
  const { data: enrolmentPolicy, isLoading: isEnrolmentPolicyLoading } = useGetSessionPolicy({
    enrolmentId: enrolmentSfid,
  });
  const schoolCurrentHoliday = useSelector((state: InitialState) => getSchoolCurrentHoliday(state));
  const enrolmentCanBePaused = useCanPauseEnrolment(enrolmentSfid, enrolmentPolicy);

  useEffect(() => {
    if (!isEnrolmentPolicyLoading && !isEnrolmentDetailsLoading) {
      setOptions(
        generateOptions({
          enrolmentId,
          tutorName,
          enrolmentType,
          enrolmentStatus,
          history,
          cancelEnrolmentBasePath,
          enrolmentPolicy,
          schoolCurrentHoliday,
          enrolmentCanBePaused,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnrolmentPolicyLoading, isEnrolmentDetailsLoading]);

  return (
    <EnrolmentLayout
      isLoading={isEnrolmentDetailsLoading || isEnrolmentPolicyLoading}
      error={error}
      sectionHeading={`${studentName}'s ${enrolmentDiscipline}`}
      mainHeading="Session times and schedules"
      enrolmentId={enrolmentId}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
    >
      <div className="flex min-h-screen flex-col px-5 pb-10 md:px-10">
        <div className="container mr-auto flex flex-col gap-y-10 px-0 pb-16 md:gap-y-10">
          {isEnrolmentPolicyLoading ? (
            <Loader />
          ) : (
            <>
              <IntentIntro className="!mb-0">How can we help with {possessiveNoun} session schedule?</IntentIntro>
              <ul className="list-none">
                {options.map(({ label, linkTo, onClickHandler, type, posthogEvent }) => {
                  return (
                    <li key={label}>
                      <OptionCard
                        linkTo={linkTo}
                        onClickHandler={() => {
                          if (!!posthogEvent) {
                            posthog.capture(posthogEvent);
                          }
                          onClickHandler?.();
                        }}
                        type={type}
                        capturePosthogEvent={
                          !!posthogEvent
                            ? () => {
                                posthog.capture(posthogEvent);
                              }
                            : undefined
                        }
                      >
                        <OptionCardContent>{label}</OptionCardContent>
                      </OptionCard>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </EnrolmentLayout>
  );
};
